import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Chip, Typography } from '@mui/material';

import { cn } from '@/shared/lib/cn';

import { expiryStatusOf, formatExpiry, type Card } from './card';
import { CardActionsMenu } from './CardActionsMenu';
import { brandFaceOf } from './cardBrand';
import { CardBrandMark } from './CardBrandMark';

interface CreditCardProps {
  card: Card;
  isDefault: boolean;
  onDelete: (card: Card) => void;
  onMakeDefault: (card: Card) => void;
  isBusy: boolean;
}

/**
 * One saved card, drawn as a card.
 *
 * The face is a fixed 1.586 aspect ratio — the real ISO/IEC 7810 ID-1 ratio —
 * so it reads as a payment card at any width rather than as a coloured box.
 * Each network gets its own gradient (see `cardBrand.ts`), because a wallet
 * where every card is the same colour forces a director to read the small
 * wordmark to tell a Visa from a UnionPay.
 *
 * The default card carries a ring and a chip; every other card is quiet, which
 * is what makes "in use" legible at a glance.
 */
export const CreditCard = ({
  card,
  isDefault,
  onDelete,
  onMakeDefault,
  isBusy,
}: CreditCardProps) => {
  const expiry = expiryStatusOf(card);
  const face = brandFaceOf(card.brand);
  const label = `${face.label} ending ${card.last4}`;

  return (
    <li className="flex flex-col gap-1.5">
      <div
        className={cn(
          'relative flex aspect-[1.586/1] w-full flex-col justify-between overflow-hidden',
          'rounded-xl p-3.5 text-white shadow-card',
          'bg-linear-135',
          face.gradient,
          // The default card is lifted out of the row by a ring rather than a
          // different colour, so the distinction survives a brand palette change.
          isDefault && 'ring-2 ring-primary-dark ring-offset-2 ring-offset-surface',
        )}
      >
        {/* A soft diagonal sheen, the way light falls across a real card.
            Purely visual, so it is hidden from the a11y tree. */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-linear-160 from-white/12 via-transparent to-black/20"
        />

        <div className="relative flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <CardChip />
            <ContactlessMark />
          </div>
          <CardBrandMark brand={card.brand} className="shrink-0" />
        </div>

        <div className="relative flex flex-col gap-0.5">
          {/* Presentational only — the accessible name comes from the actions
              below, so a screen reader is not read four groups of bullets. */}
          <Typography
            aria-hidden
            variant="body1"
            className="text-[15px] font-semibold tracking-[0.14em] tabular-nums"
          >
            •••• •••• •••• {card.last4}
          </Typography>

          <div className="flex items-end justify-between gap-2">
            <div className="min-w-0">
              <Typography
                variant="caption"
                component="p"
                className="text-[10px] tracking-wider text-white/70 uppercase"
              >
                Expires
              </Typography>
              <Typography variant="body2" className="tabular-nums">
                {formatExpiry(card)}
              </Typography>
            </div>

            {isDefault && (
              <Chip
                size="small"
                icon={<CheckCircleIcon />}
                label="In use"
                className="h-6 bg-white/20 text-white [&_.MuiChip-icon]:text-white"
              />
            )}
          </div>
        </div>
      </div>

      {/* Actions sit below the face, not on it: a 44px control overlaid on a
          card that is itself only ~180px tall leaves no room for the number.
          The left slot carries the card's standing — an expiry warning, or
          which card this is — and every action lives in the overflow menu. */}
      <div className="flex min-h-9 items-center justify-between gap-2">
        {expiry === 'expired' ? (
          <Typography variant="caption" color="error" role="status">
            Expired
          </Typography>
        ) : expiry === 'expiring' ? (
          <Typography variant="caption" color="warning.main" role="status">
            Expires soon
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        )}

        <CardActionsMenu
          card={card}
          isDefault={isDefault}
          isBusy={isBusy}
          onMakeDefault={onMakeDefault}
          onDelete={onDelete}
        />
      </div>
    </li>
  );
};

/** The EMV contact plate. Drawn with the contact pads real chips have — a plain
 *  gold rectangle is the detail that makes a rendered card look fake. */
const CardChip = () => (
  <svg
    viewBox="0 0 32 24"
    aria-hidden
    className="h-6 w-8 shrink-0 drop-shadow-sm"
    role="presentation"
  >
    <rect width="32" height="24" rx="4" fill="url(#chipFace)" />
    <g stroke="rgb(0 0 0 / 0.28)" strokeWidth="1.1" fill="none">
      <path d="M11 0v24M21 0v24M0 8h32M0 16h32" />
      <rect x="11" y="8" width="10" height="8" rx="1.5" fill="rgb(0 0 0 / 0.06)" />
    </g>
    <defs>
      <linearGradient id="chipFace" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#f5e2a8" />
        <stop offset="50%" stopColor="#d9b96a" />
        <stop offset="100%" stopColor="#b8923f" />
      </linearGradient>
    </defs>
  </svg>
);

/** The contactless symbol — four arcs radiating right, as printed on every
 *  tap-to-pay card. */
const ContactlessMark = () => (
  <svg
    viewBox="0 0 16 20"
    aria-hidden
    className="h-5 w-4 shrink-0 text-white/70"
    role="presentation"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
  >
    <path d="M2 6a7 7 0 0 1 0 8M6 4a11 11 0 0 1 0 12M10 2.5a15 15 0 0 1 0 15" />
  </svg>
);
